import React, { forwardRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus, ChevronLeft, ChevronRight } from 'lucide-react';

/**
 * FloatingButton: 전역 통일된 플로팅 버튼 컴포넌트
 * - variant: 'plus' | 'prev' | 'next'
 * - onClick, title, disabled: 동작 동일 유지
 * - usePortal: 기존 페이지 동작을 유지하기 위해 선택적으로 포털 사용
 * - size: 필요 시 크기 강제 지정 (기본: plus=56, prev/next=48)
 * - bottom: 위치 통일 기본값을 따르되, 필요 시 재정의 가능
 * - leftOffsetPx: 특별한 경우 가로 오프셋이 필요하면 사용 (기본 0)
 */
const FloatingButton = forwardRef(function FloatingButton(
  {
    variant = 'plus',
    onClick,
    title,
    disabled = false,
    usePortal = true,
    size,
    bottom,
    leftOffsetPx = 0,
    className = '',
    icon,
    selected = false,
  },
  ref
) {
  const isPlus = variant === 'plus';
  const isPrev = variant === 'prev';
  const isNext = variant === 'next';

  const computedSize = size ?? (isPlus ? 56 : 48);
  const defaultBottom = bottom ?? 'calc(68px + 2rem + env(safe-area-inset-bottom, 0px))';

  const interactiveStateClasses = disabled ? 'opacity-50 cursor-not-allowed' : 'hover:opacity-95 active:opacity-90';
  const glassClasses = 'bg-glass-white backdrop-blur-sm';
  const selectedClasses = selected ? 'ring-2 ring-primary shadow-xl' : '';
  const interactionClasses = 'cursor-pointer hover:shadow-xl hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 active:shadow-md active:scale-90 active:ring-4 active:ring-primary/70 active:ring-offset-2 active:ring-offset-white';
  const baseClasses = `fixed z-[1000] rounded-full shadow-lg transition-all duration-200 transform-gpu select-none ${interactionClasses} ${selectedClasses} ${glassClasses} ${interactiveStateClasses} ${className}`.trim();

  const positionStyle = isPlus
    ? {
        left: `calc(50% + ${leftOffsetPx}px)`,
        transform: 'translateX(-50%)',
      }
    : isPrev
      ? { left: '1rem' }
      : { right: '1rem' };

  const style = {
    bottom: defaultBottom,
    width: `${computedSize}px`,
    height: `${computedSize}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    border: 'none',
    outline: 'none',
    // 글래스모피즘: 반투명 + 백드롭 블러 + 하이라이트 림
    backgroundImage: [
      // 밝은 외곽 링 (유리 림)
      'radial-gradient(circle at 50% 50%, rgba(255,255,255,0) 68%, rgba(255,255,255,0.92) 74%, rgba(255,255,255,0) 82%)',
      // 얕은 곡면 음영 링 (돔 가장자리)
      'radial-gradient(circle at 50% 52%, rgba(0,0,0,0) 70%, rgba(0,0,0,0.12) 88%, rgba(0,0,0,0) 96%)'
    ].join(', '),
    backgroundRepeat: 'no-repeat, no-repeat',
    backgroundSize: 'auto, auto',
    backgroundPosition: 'center, center',
    backgroundBlendMode: 'normal, normal',
    // 얇은 내부 림만 유지 (중앙은 평평하게)
    boxShadow: '0 8px 20px rgba(0,0,0,0.08), inset 0 0 0 2px rgba(255,255,255,0.12)',
    ...positionStyle,
  };

  const iconColorClass = disabled ? 'text-gray-500' : 'text-primary';
  const DefaultIcon = isPlus ? (
    <Plus size={isPlus ? 24 : 20} strokeWidth={3} className={iconColorClass} />
  ) : isPrev ? (
    <ChevronLeft size={24} strokeWidth={3} className={iconColorClass} />
  ) : (
    <ChevronRight size={24} strokeWidth={3} className={iconColorClass} />
  );

  const [isPressed, setIsPressed] = useState(false);
  const pressedClasses = isPressed && !disabled ? 'scale-90 ring-4 ring-primary/70 ring-offset-2 ring-offset-white shadow-md' : '';

  const button = (
    <button
      ref={ref}
      onClick={onClick}
      title={title}
      disabled={disabled}
      aria-pressed={selected ? 'true' : 'false'}
      className={`${baseClasses} ${pressedClasses}`}
      style={{ ...style, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation' }}
      onPointerDown={() => setIsPressed(true)}
      onPointerUp={() => setIsPressed(false)}
      onPointerCancel={() => setIsPressed(false)}
      onPointerLeave={() => setIsPressed(false)}
      onBlur={() => setIsPressed(false)}
    >
      {icon ?? DefaultIcon}
    </button>
  );

  if (usePortal && typeof document !== 'undefined') {
    return createPortal(button, document.body);
  }

  return button;
});

export default FloatingButton;


